import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { collection, getDocs, doc, updateDoc, getDoc, addDoc, query, where } from 'firebase/firestore';
import { LuClipboardList, LuFilterX, LuLoaderCircle, LuPlus, LuSave } from 'react-icons/lu';
import { toast } from 'react-toastify';
import { db } from '../../config/firebaseConfig';
import Button from '../../components/ui/Button';
import EntryGrid from './parts/EntryGrid';
import { hastaneEntryColumns } from './parts/entryColumns';
import { EntryCount, EntryEmpty, EntryFooterInfo } from './parts/entryBits';
import useSmoText from './smoText';

const createBlankRow = () => ({
  _isNew: true,
  _visitId: null,
  projectId: '',
  merkez: '',
  visitNumber: '',
  visitType: 'hastane',
  siteCoordinator: '',
  patientId: '',
  sourceDocument: '', // Kaynak Döküman
  sourceDocumentCompletionTime: '', // Kaynak Döküman Tamamlanma Saati
  screenFail: '',
  dropPatient: '',
  visitDate: '',
  visitStartTime: '',
  protocolDeviation: '',
  capaOpened: '',
  comments: '',
  ecrfCompletionDate: '',
  ecrfCompletionTime: '', // Yeni eCRF tamamlanma saati alanı
  sourceDocCompletionDate: '',
  sourceDocCompletionTime: '', // Yeni Kaynak Dok. tamamlanma saati alanı
  signatureCompletionDate: '',
  processEndDate: '',
  slaTarget: '',
  issueOccurred: '',
});

const visitToRow = (v) => {
  const ev = v.visitEvaluation || {};
  return {
    _isNew: false,
    _visitId: v.id,
    projectId: v.projectId || '',
    merkez: v.hospital || v.merkez || v.city || '',
    visitNumber: v.visitNumber || '',
    visitType: v.visitType || 'hastane',
    siteCoordinator: v.selectedCra || v.doctor || '',
    patientId: v.patientId || '',
    sourceDocument: v.sourceDocument || '', // Kaynak Döküman
    sourceDocumentCompletionTime: v.sourceDocumentCompletionTime || '', // Kaynak Döküman Tamamlanma Saati
    screenFail: ev.screenFail || '',
    dropPatient: ev.dropPatient || '',
    visitDate: v.firstVisitDate || v.date || '',
    visitStartTime: v.startTime || '',
    protocolDeviation: ev.protocolDeviation || '',
    capaOpened: ev.capaOpened || '',
    comments: ev.comments || '',
    ecrfCompletionDate: ev.ecrfCompletionDate || '',
    ecrfCompletionTime: ev.ecrfCompletionTime || '', // Yeni eCRF tamamlanma saati alanı
    sourceDocCompletionDate: ev.sourceDocCompletionDate || '',
    sourceDocCompletionTime: ev.sourceDocCompletionTime || '', // Yeni Kaynak Dok. tamamlanma saati alanı
    signatureCompletionDate: ev.signatureCompletionDate || '',
    processEndDate: ev.processEndDate || '',
    slaTarget: ev.slaTarget || '',
    issueOccurred: ev.issueOccurred || '',
  };
};

const rowToFirestore = (row, employeeId, employeeName, projectId) => ({
  patientId: row.patientId,
  visitNumber: row.visitNumber,
  visitType: row.visitType || 'hastane',
  date: row.visitDate,
  firstVisitDate: row.visitDate,
  startTime: row.visitStartTime,
  hospital: row.merkez,
  merkez: row.merkez,
  selectedCra: row.siteCoordinator,
  sourceDocument: row.sourceDocument, // Kaynak Döküman
  sourceDocumentCompletionTime: row.sourceDocumentCompletionTime, // Kaynak Döküman Tamamlanma Saati
  projectId: projectId || row.projectId || '',
  employeeId,
  employeeName,
  lastUpdated: new Date().toISOString(),
  visitEvaluation: {
    screenFail: row.screenFail,
    dropPatient: row.dropPatient,
    protocolDeviation: row.protocolDeviation,
    capaOpened: row.capaOpened,
    comments: row.comments,
    ecrfCompletionDate: row.ecrfCompletionDate,
    ecrfCompletionTime: row.ecrfCompletionTime, // Yeni eCRF tamamlanma saati alanı
    sourceDocCompletionDate: row.sourceDocCompletionDate,
    sourceDocCompletionTime: row.sourceDocCompletionTime, // Yeni Kaynak Dok. tamamlanma saati alanı
    signatureCompletionDate: row.signatureCompletionDate,
    processEndDate: row.processEndDate,
    slaTarget: row.slaTarget,
    issueOccurred: row.issueOccurred,
  },
});

const calcCycleTime = (visitDate, processEndDate) => {
  if (!visitDate || !processEndDate) return '';
  const d1 = new Date(visitDate);
  const d2 = new Date(processEndDate);
  if (isNaN(d1) || isNaN(d2)) return '';
  return String(Math.floor((d2 - d1) / (1000 * 60 * 60 * 24)));
};

const EMPTY_FILTERS = {
  merkez: '',
  visitNumber: '',
  visitType: '',
  siteCoordinator: '',
  patientId: '',
  sourceDocument: '', // Kaynak Döküman filtresi
  sourceDocumentCompletionTime: '', // Kaynak Döküman Saati filtresi
  screenFail: '',
  dropPatient: '',
  visitDateFrom: '', visitDateTo: '',
  protocolDeviation: '',
  capaOpened: '',
  comments: '',
  ecrfCompletionDateFrom: '', ecrfCompletionDateTo: '',
  sourceDocCompletionDateFrom: '', sourceDocCompletionDateTo: '',
  signatureCompletionDateFrom: '', signatureCompletionDateTo: '',
  processEndDateFrom: '', processEndDateTo: '',
  slaTarget: '',
  issueOccurred: '',
};

const HastaneDataEntryContent = ({ workers, selectedSMO, selectedWorkerId, selectedProjectId, onDataSaved }) => {
  const { label } = useSmoText();
  // Toasts in callbacks read the current language through a ref
  const labelRef = useRef(label);
  labelRef.current = label;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const loadIdRef = useRef(0);
  const [hospitals, setHospitals] = useState([]);
  const [cras, setCras] = useState([]);

  // Fetch Centers and CRAs on mount
  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const centersSnap = await getDocs(collection(db, 'Centers'));
        const hospitalsList = centersSnap.docs
          .map(d => ({ id: d.id, name: d.data()['Adı'] || d.id }))
          .sort((a, b) => a.name.localeCompare(b.name, 'tr'));
        setHospitals(hospitalsList);

        const extSnap = await getDocs(collection(db, 'ExternalPersonnels'));
        const crasList = extSnap.docs
          .filter(d => {
            const data = d.data();
            const unvan = (data['Ünvan'] || '').toUpperCase();
            const personnelType = (data['Personel Tipi'] || '').toUpperCase();
            const status = (data['Durum'] || '').toLowerCase();
            return status === 'aktif' && (unvan.includes('CRA') || personnelType.includes('CRA'));
          })
          .map(d => {
            const data = d.data();
            return {
              id: d.id,
              name: `${data['Adı'] || ''} ${data['Soyadı'] || ''}`.trim(),
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name, 'tr'));
        setCras(crasList);
      } catch (err) {
        console.error('Error fetching centers/CRAs:', err);
      }
    };
    fetchLookups();
  }, []);

  const hospitalMap = useMemo(() => {
    const map = {};
    hospitals.forEach(h => { map[h.id] = h.name; });
    return map;
  }, [hospitals]);

  const craMap = useMemo(() => {
    const map = {};
    cras.forEach(c => { map[c.id] = c.name; });
    return map;
  }, [cras]);

  const getHospitalName = useCallback((id) => hospitalMap[id] || id || '—', [hospitalMap]);
  const getCraName = useCallback((id) => craMap[id] || id || '—', [craMap]);

  const selectedWorker = workers.find(w => w.id === selectedWorkerId);
  const workerProjects = selectedWorker?.projects || [];

  const getProjectName = useCallback((projectId) => {
    const proj = workerProjects.find(p => p.projectId === projectId);
    return proj ? proj.projectTitle : projectId || '—';
  }, [workerProjects]);

  const setFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const hasActiveFilters = Object.values(filters).some(v => v !== '');
  const clearFilters = () => setFilters(EMPTY_FILTERS);
  const toggleFilters = useCallback(() => setShowFilters(prev => !prev), []);

  const loadVisits = useCallback(async () => {
    if (!selectedWorkerId) { setRows([]); return; }
    const myLoad = ++loadIdRef.current;
    setLoading(true);
    try {
      const visitsRef = collection(db, 'Visits');
      const q = selectedProjectId
        ? query(visitsRef, where('employeeId', '==', selectedWorkerId), where('projectId', '==', selectedProjectId), where('visitType', '==', 'hastane'))
        : query(visitsRef, where('employeeId', '==', selectedWorkerId), where('visitType', '==', 'hastane'));
      const snap = await getDocs(q);
      if (myLoad !== loadIdRef.current) return;
      const visitRows = snap.docs.map(d => visitToRow({ id: d.id, ...d.data() }));
      visitRows.sort((a, b) => (a.visitDate || '').localeCompare(b.visitDate || ''));
      setRows(visitRows);
    } catch (err) {
      if (myLoad !== loadIdRef.current) return;
      console.error('Error loading hastane visits:', err);
      toast.error(labelRef.current('entry.toast.loadError'));
    } finally {
      if (myLoad !== loadIdRef.current) return;
      setLoading(false);
    }
  }, [selectedWorkerId, selectedProjectId]);

  useEffect(() => {
    if (selectedWorkerId) {
      loadVisits();
    } else {
      setRows([]);
    }
  }, [selectedWorkerId, loadVisits]);

  useEffect(() => {
    setFilters(EMPTY_FILTERS);
  }, [selectedWorkerId, selectedProjectId]);

  const filteredRows = useMemo(() => {
    const dateInRange = (val, from, to) => {
      if (!from && !to) return true;
      if (!val) return false;
      if (from && val < from) return false;
      if (to && val > to) return false;
      return true;
    };
    return rows
      .map((row, originalIndex) => ({ row, originalIndex }))
      .filter(({ row }) => {
        if (filters.merkez) {
          const merkezName = (hospitalMap[row.merkez] || row.merkez || '').toLowerCase();
          if (!merkezName.includes(filters.merkez.toLowerCase())) return false;
        }
        if (filters.visitNumber && !String(row.visitNumber).toLowerCase().includes(filters.visitNumber.toLowerCase())) return false;
        if (filters.visitType && row.visitType !== filters.visitType) return false;
        if (filters.siteCoordinator) {
          const craName = (craMap[row.siteCoordinator] || row.siteCoordinator || '').toLowerCase();
          if (!craName.includes(filters.siteCoordinator.toLowerCase())) return false;
        }
        if (filters.patientId && !row.patientId.toLowerCase().includes(filters.patientId.toLowerCase())) return false;
        if (filters.screenFail && row.screenFail !== filters.screenFail) return false;
        if (filters.dropPatient && row.dropPatient !== filters.dropPatient) return false;
        if (filters.protocolDeviation && row.protocolDeviation !== filters.protocolDeviation) return false;
        if (filters.capaOpened && row.capaOpened !== filters.capaOpened) return false;
        if (filters.comments && !row.comments.toLowerCase().includes(filters.comments.toLowerCase())) return false;
        if (filters.slaTarget && !String(row.slaTarget).includes(filters.slaTarget)) return false;
        if (filters.issueOccurred && row.issueOccurred !== filters.issueOccurred) return false;
        if (!dateInRange(row.visitDate, filters.visitDateFrom, filters.visitDateTo)) return false;
        if (!dateInRange(row.ecrfCompletionDate, filters.ecrfCompletionDateFrom, filters.ecrfCompletionDateTo)) return false;
        if (!dateInRange(row.sourceDocCompletionDate, filters.sourceDocCompletionDateFrom, filters.sourceDocCompletionDateTo)) return false;
        if (!dateInRange(row.signatureCompletionDate, filters.signatureCompletionDateFrom, filters.signatureCompletionDateTo)) return false;
        if (!dateInRange(row.processEndDate, filters.processEndDateFrom, filters.processEndDateTo)) return false;
        return true;
      });
  }, [rows, filters, hospitalMap, craMap]);

  const handleCellChange = (index, field, value) => {
    setRows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addRow = () => setRows(prev => [...prev, createBlankRow()]);
  const removeRow = (index) => setRows(prev => prev.filter((_, i) => i !== index));

  const handleSave = async () => {
    if (rows.length === 0) { toast.warn(labelRef.current('entry.toast.nothingToSave')); return; }
    if (!selectedWorkerId) { toast.warn(labelRef.current('entry.toast.pickWorker')); return; }

    const worker = workers.find(w => w.id === selectedWorkerId);
    const employeeName = worker ? `${worker.name} ${worker.surname}` : '';

    setSaving(true);
    let savedCount = 0, errorCount = 0;

    try {
      for (const row of rows) {
        const data = rowToFirestore(row, selectedWorkerId, employeeName, selectedProjectId);
        try {
          if (row._visitId && !row._isNew) {
            const visitRef = doc(db, 'Visits', row._visitId);
            const currentDoc = await getDoc(visitRef);
            if (currentDoc.exists()) {
              const merged = {
                ...currentDoc.data(),
                ...data,
                visitEvaluation: { ...currentDoc.data().visitEvaluation, ...data.visitEvaluation },
              };
              await updateDoc(visitRef, merged);
            } else {
              await updateDoc(visitRef, data);
            }
            savedCount++;
          } else {
            await addDoc(collection(db, 'Visits'), { ...data, createdAt: new Date().toISOString(), manualEntry: true });
            savedCount++;
          }
        } catch (err) {
          console.error('Error saving row:', err);
          errorCount++;
        }
      }
      if (savedCount > 0) {
        toast.success(labelRef.current('entry.toast.saved', { count: savedCount }));
        if (onDataSaved) onDataSaved();
        await loadVisits();
      }
      if (errorCount > 0) toast.error(labelRef.current('entry.toast.saveFailed', { count: errorCount }));
    } catch (err) {
      console.error('Save error:', err);
      toast.error(labelRef.current('entry.toast.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const existingCount = rows.filter(r => !r._isNew).length;
  const newCount = rows.filter(r => r._isNew).length;

  // Cycle time and the SLA answers are calculated, not typed (same rules as before)
  const derived = (row) => {
    const cycleTime = calcCycleTime(row.visitDate, row.processEndDate);
    const slaMet = cycleTime !== '' && row.slaTarget !== ''
      ? (Number(cycleTime) <= Number(row.slaTarget) ? 'Evet' : 'Hayır') : '';
    const slaOverdue = cycleTime !== '' && row.slaTarget !== ''
      ? (Number(cycleTime) > Number(row.slaTarget) ? 'Evet' : 'Hayır') : '';
    return { cycleTime, slaMet, slaOverdue };
  };
  const { groups, columns } = hastaneEntryColumns({ label, hospitals, cras, getProjectName, selectedProjectId, change: handleCellChange, derived });

  return (
    <>
      {/* Stats / Actions bar */}
      {(rows.length > 0 || hasActiveFilters || selectedWorkerId) && (
        <div className="tw-flex tw-shrink-0 tw-flex-wrap tw-items-center tw-gap-3 tw-border-b tw-border-slate-200/80 tw-px-4 tw-py-2.5 sm:tw-px-6 dark:tw-border-white/10">
          {rows.length > 0 && (
            <EntryCount shown={filteredRows.length} total={rows.length} filtered={hasActiveFilters} existing={existingCount} added={newCount} />
          )}
          {hasActiveFilters && (
            <Button size="sm" variant="ghost" onClick={clearFilters} title={label('entry.clearFiltersHint')}>
              <LuFilterX aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('entry.clearFilters')}
            </Button>
          )}
          {selectedWorkerId && (
            <Button size="sm" variant="success" onClick={addRow}>
              <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('entry.addRow')}
            </Button>
          )}
          <span className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('entry.siteHelp')}</span>
        </div>
      )}

      {/* Table */}
      <div className="tw-min-h-[16rem] tw-flex-1 tw-overflow-auto glass-scrollbar">
        {loading ? (
          <EntryEmpty loading text={label('entry.loading')} />
        ) : !selectedWorkerId ? (
          <EntryEmpty icon={LuClipboardList} text={label('entry.pickWorker')} />
        ) : rows.length === 0 ? (
          <EntryEmpty icon={LuClipboardList} text={label('entry.noSiteVisits')} actionLabel={label('entry.addRow')} onAction={addRow} />
        ) : (
          <EntryGrid
            groups={groups}
            columns={columns}
            rows={filteredRows}
            showFilters={showFilters}
            toggleFilters={toggleFilters}
            filters={filters}
            setFilter={setFilter}
            onRemove={removeRow}
          />
        )}
      </div>

      {/* Footer of the Hastane tab: its own save */}
      <div className="tw-flex tw-shrink-0 tw-flex-wrap tw-items-center tw-justify-end tw-gap-2 tw-border-t tw-border-slate-200/80 tw-px-4 tw-py-2.5 sm:tw-px-6 dark:tw-border-white/10">
        <div className="tw-mr-auto tw-min-w-0">
          {rows.length > 0 && (
            <EntryFooterInfo shown={filteredRows.length} total={rows.length} filtered={hasActiveFilters} existing={existingCount} added={newCount} />
          )}
        </div>
        <Button variant="primary" onClick={handleSave} disabled={saving || rows.length === 0}>
          {saving ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
          {saving ? label('entry.saving') : label('entry.saveAll')}
        </Button>
      </div>
    </>
  );
};

HastaneDataEntryContent.propTypes = {
  workers: PropTypes.array.isRequired,
  selectedSMO: PropTypes.object,
  selectedWorkerId: PropTypes.string,
  selectedProjectId: PropTypes.string,
  onDataSaved: PropTypes.func,
};

export default HastaneDataEntryContent;
